import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  OnDestroy,
  ViewChild,
  ElementRef,
  AfterViewInit,
  ChangeDetectorRef,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Subject, Observable, of } from 'rxjs';
import { take } from 'rxjs/operators';

import { AuthService } from '@app/core/auth';

// TODO: Import actual AuthService path confirmed in Task 1.3
// import { AuthService } from '@app/core/auth';

@Component({
  selector: 'kt-embed-replit',
  templateUrl: './embed-replit.component.html',
  styleUrls: ['./embed-replit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmbedReplitComponent implements OnInit, OnDestroy, AfterViewInit {
  // Update to the new test Replit app URL
  replitBaseUrl :string =
    'https://quick-checkout-brendonbeebe.replit.app?embed=true';

  // Step 2.3: Variable to hold the sanitized URL for the iframe
  safeReplitUrl :SafeResourceUrl | null = null;
  private expectedOrigin :string | null = null;

  // Get a reference to the iframe element
  @ViewChild('replitIframe') private iframeRef! :ElementRef<HTMLIFrameElement>;

  // Subject to manage listener cleanup
  readonly #destroy$ = new Subject<void>();

  // TODO: Inject AuthService and DomSanitizer
  constructor(
    private sanitizer :DomSanitizer,
    private authService :AuthService,
    private cd :ChangeDetectorRef,
  ) {}

  ngOnInit() :void {
    this.calculateExpectedOrigin();
    this.createSafeUrlWithMockApiToken();
  }

  ngAfterViewInit() :void {
    // Add listener after the view (and iframe) is initialized
    window.addEventListener('message', this.handleIframeMessage.bind(this));
  }

  ngOnDestroy() :void {
    // Clean up listener
    window.removeEventListener('message', this.handleIframeMessage.bind(this));
    this.#destroy$.next();
    this.#destroy$.complete();
  }

  private calculateExpectedOrigin() :void {
    try {
      const url = new URL(this.replitBaseUrl);
      this.expectedOrigin = url.origin;
      console.log('Expected iframe origin:', this.expectedOrigin);
    } catch (e) {
      console.error('Could not parse replitBaseUrl to determine origin:', e);
      this.expectedOrigin = null; // Cannot verify origin if URL is invalid
    }
  }

  private createSafeUrlWithMockApiToken() :void {
    const parentOrigin = window.location.origin;

    this.getMockFirstApiToken()
      .pipe(take(1))
      .subscribe((apiToken) => {
        if (apiToken && parentOrigin) {
          const urlWithParams = new URL(this.replitBaseUrl);
          urlWithParams.searchParams.set('token', apiToken);
          urlWithParams.searchParams.set('parentOrigin', parentOrigin);

          const fullUrl = urlWithParams.toString();
          console.log('Constructed iframe URL with MOCKED API token:', fullUrl);

          this.safeReplitUrl =
            this.sanitizer.bypassSecurityTrustResourceUrl(fullUrl);
          this.cd.detectChanges();
        } else {
          console.error(
            'Mock API token or parent origin not found, cannot embed Replit.',
          );
          this.safeReplitUrl = null;
          this.cd.detectChanges();
        }
      });
  }

  private getMockFirstApiToken() :Observable<string | null> {
    console.log('[Mock] Returning specific mock API token');
    const mockToken = '40efd160-1444-479f-a7cb-7b681f5e3665';

    return of(mockToken);
  }

  // Handle messages received from the iframe
  private handleIframeMessage(event :MessageEvent) :void {
    // --- Security Check: Verify Origin ---
    if (!this.expectedOrigin || event.origin !== this.expectedOrigin) {
      console.warn(
        `Message ignored from unexpected origin: ${event.origin}. Expected: ${this.expectedOrigin}`,
      );

      return;
    }

    const messageData = event.data;
    console.log('Message received from iframe:', messageData);

    // Check message type - Only handle GET_USER_INFO now
    if (messageData && messageData.type === 'GET_USER_INFO') {
      this.sendUserInfoToIframe();
    } else if (messageData && messageData.type) {
      // Log other message types but don't process them here
      console.warn(
        `Received unhandled postMessage type from iframe: ${messageData.type}`,
      );
    }
  }

  // Send user info back to the iframe
  private sendUserInfoToIframe() :void {
    const user = this.authService.getUser();
    // Use the helper function to send
    this.sendToIframe({ type: 'USER_INFO_RESPONSE', payload: user || null });
  }

  // Placeholder methods for new actions // REMOVE THESE
  // private fetchSalesOrders(payload: any): void { ... }
  // private tagSalesOrder(payload: { orderId: any, tag: string }): void { ... }
  // private untagSalesOrder(payload: { orderId: any, tag: string }): void { ... }

  // Helper to send messages to iframe consistently
  private sendToIframe(message :any) :void {
    const iframeWindow = this.iframeRef?.nativeElement?.contentWindow;
    if (iframeWindow && this.expectedOrigin) {
      console.log(
        `[Angular] Sending message to iframe (${this.expectedOrigin}):`,
        message,
      );
      iframeWindow.postMessage(message, this.expectedOrigin);
    } else {
      console.error(
        '[Angular] Cannot send message: Iframe content window or expected origin not available.',
      );
    }
  }
}
